<template>
  <div>
    <div>
      <div class="cards__container">
        <div :class="['overlay', {active: isLoading}]">
          <loading-spinner
            custom-id="loading_spinner_table_view"
            :loading-status="isLoading"
          />
        </div>
        <div
          v-if="templates.count"
          :class="['cards d-flex flex-wrap justify-content-between', { loading: isLoading }]"
        >
          <TemplateCard
            v-for="card in templates.result"
            :key="card.id"
            :info="card"
            :on-preview="onPreview"
            :details="details"
            :edit="edit"
            :clone="clone"
            :remove="() => openDialog(card.id)"
          />
        </div>
        <div v-else-if="!isLoading">
          <NoData />
        </div>
      </div>
      <div class="d-flex flex-wrap justify-content-between">
        <ejs-custom-pager
          id="templateCardsEjsPager"
          :on-pager-click="onPagerClick"
          :drop-down-changed="onPageSizesChange"
          :paging="paging"
          :page-sizes="pageSizes"
        />
      </div>
      <BaseDialog
        ref="previewTemplateDialog"
        title="Preview"
        :is-iframe="true"
        :content="templateContent"
        :custom-content-template="customContentTemplate"
        :on-close="onCloseDialog"
        width="90%"
        height="90%"
      />
    </div>
    <BaseDialog
      ref="deleteDialog"
      :content="'Are you sure you wish to delete this template?'"
      :submit-button="deleteButton"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import { Pager, PagerDropDown } from '@syncfusion/ej2-grids'
import NoData from '@/components/NoData'
import BaseDialog from '@/components/Dialog/BaseDialog'
import LoadingSpinner from '@/components/LoadingSpinner'
import { mapGetters } from 'vuex'
import TemplateCard from './TemplateCard'
import TemplatePreview from '../TemplatePreview'
import EjsCustomPager from '../Ejs/EjsCustomPager.vue'
import { iframeParentCommunication } from '@/mixins/iframeParentCommunication'

Pager.Inject(PagerDropDown)

export default {
  components: {
    TemplateCard,
    NoData,
    BaseDialog,
    EjsCustomPager,
    LoadingSpinner
  },
  mixins: [iframeParentCommunication],
  props: {
    details: Function,
    edit: Function,
    clone: Function,
    remove: Function
  },
  data () {
    return {
      pageSizes: [12, 24, 48, 90],
      customContentTemplate: TemplatePreview,
      selectedTemplate: null,
      pristine: true,
      items: [
        {
          text: 'Last Updated',
          name: 'updateDate',
          direction: 'descending'
        },
        {
          text: 'First Updated',
          name: 'updateDate',
          direction: 'ascending'
        },
        {
          text: 'A to Z',
          name: 'name',
          direction: 'ascending'
        },
        {
          text: 'Z to A',
          name: 'name',
          direction: 'descending'
        },
        {
          text: 'Status',
          name: 'active',
          direction: 'descending'
        },
        {
          text: 'Editor',
          name: 'editorTypeId',
          direction: 'ascending'
        }
      ],
      templateBuilderQuery: this.$helpers.getTemplateBuilderQuery(this.$route),
      typingTimer: null,
      deleteButton: {
        label: 'Ok',
        class: 'btn-danger',
        action: this.onConfirm
      },
      selectedForDeletion: -1
    }
  },
  computed: {
    ...mapGetters({
      templates: 'getTemplates',
      templateContent: 'getTemplateContent',
      paging: 'getPaging',
      isLoading: 'getLoadingStatus',
      activeType: 'getActiveType'
    }),
    currentOrder () {
      const { order } = this.paging
      if (order) {
        const sort = this.items.find(item => item.name === order.column && item.direction.startsWith(order.direction))
        if (sort) {
          return sort.text
        }
      }
      return ''
    }
  },
  beforeCreate () {
    this.$store.commit('SET_GRIDSELECTEDTEMPLATE', {})
    this.$store.commit('SET_TEMPLATE_PREVIEW_CONTENT', null)
  },
  created () {
    const currentPageSize = this.paging.pageSize
    const pageSize = this.getPageSizes()
    this.$store.commit('SET_PAGING', { ...this.paging, pageSizes: this.pageSizes, pageSize: pageSize })
    if (currentPageSize !== pageSize) {
      setTimeout(() => {
        this.refresh()
      }, 100)
    }
  },
  methods: {
    getPageSizes () {
      const pageSizeIndex = this.paging.pageSizes.findIndex(x => parseInt(x) === this.paging.pageSize)
      return (pageSizeIndex > -1) ? this.pageSizes[pageSizeIndex] : 12
    },
    getLoadingStatus (name) {
      return this.$store.getters.getLoadingStatus(name)
    },
    refresh () {
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      if (userEmail) {
        const { paging } = this
        const { order } = paging
        const dataState = {
          action: {
            currentPage: paging.page
          },
          skip: 0,
          take: paging.pageSize,
          sorted: [
            {
              name: order.column,
              direction: order.direction
            }
          ]
        }
        this.$store.dispatch('setTemplates', { userEmail, dataState })
      }
    },
    onPagerClick (data) {
      const { currentPage, newProp } = data
      if ('currentPage' in newProp) {
        this.$store.commit('SET_PAGING', { page: currentPage })
        Vue.nextTick(() => {
          this.refresh()
        })
      }
    },
    onPageSizesChange (data) {
      if (this.pristine) {
        this.pristine = false
      } else {
        this.$store.commit('SET_PAGING', { pageSize: data.pageSize, page: 1 })
        Vue.nextTick(() => {
          this.refresh()
        })
      }
    },
    onPreview (templateId) {
      this.$refs.previewTemplateDialog.openDialog()
      this.$store.dispatch('setTemplateOnPreview', { ...this.templateBuilderQuery, templateId })
    },
    onCloseDialog () {
      this.$store.commit('SET_TEMPLATE_PREVIEW_CONTENT', null)
    },
    openDialog (id) {
      this.selectedForDeletion = id
      this.$refs.deleteDialog.openDialog()
    },
    onConfirm () {
      if (this.selectedForDeletion > -1) {
        const prom = this.remove(this.selectedForDeletion)
        this.selectedForDeletion = -1
        prom.then(response => {
          if (response.success) {
            this.refresh()
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../../node_modules/@syncfusion/ej2-base/styles/bootstrap4.css';
  @import '../../../node_modules/@syncfusion/ej2-vue-grids/styles/bootstrap4.css';
  @import '../../../node_modules/@syncfusion/ej2-buttons/styles/bootstrap4.css';
  @import '../../../node_modules/@syncfusion/ej2-dropdowns/styles/bootstrap4.css';
  @import '../../../node_modules/@syncfusion/ej2-inputs/styles/bootstrap4.css';

  .e-control {
    font-size: 1.6rem;
    font-family: 'Source Sans Pro';
    color: #666;
  }
  .cards {
    flex-flow: row wrap;
    margin-left: -15px;
    margin-right: -15px;
    &::after{
      content: "";
      flex: auto;
    }
    &__container {
      min-height: 349px;
      position: relative;
    }
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
    display: none;
    background-color: #ffffffcf;
    &.active {
      display: block;
    }
  }

  .e-pager{
    border: 0;
    padding: 0;
  }

  .e-icons {
    color: #00ffff;
    font-size: 26px;
  }

  .e-input-group {
    flex-basis: 25%;
    height: 30.5px;
    overflow: hidden;
    &-icon {
      #btnSearch {
        display: block;
        width: 100%;
        height: 100%;
        border-radius: 0;
        border: 0;
        background-color: transparent;
        color: $black;
      }
    }
  }

  .e-input-group-icon {
    width: 33px;
    &:hover {
      border-color: $gray-400 !important;
    }
  }

  .dropdown-border-color {
    border-color: #ced4da;
  }

  .sort-by {
    vertical-align: middle;
    line-height: 30px;
    margin-bottom: .25rem;
  }

  .dropdown-sort-by {
    height: calc(1.5em + .75rem + 2px);
    margin-left: 5px;
    font-size: 1.4rem;
    padding: .25rem .5rem;
    border: 1px solid #ced4da;
    border-radius: .25rem;
    min-width: 0px;
    padding-left: 10px;
  }

  .dropdown-sort-by > .e-icon-right {
      margin-left: 10px;
  }

  .dropdown-sort-by > :first-child {
    display: none;
  }

  .dropdown-sort-by > :last-child {
    margin-left: 10px;
  }

  .multiselect-filter-select{
    max-width: 25rem;
    flex-basis: 30%;
    height: 30.5px;
    overflow: hidden;
    height: 3rem !important;
    width: 47rem !important;
  }

  .multi-select-parent{
    height: 31.5px !important;
    width: 60rem !important;
  }
  .editor-filter {
    vertical-align: middle;
    line-height: 30px;
    margin-bottom: .25rem;
    padding-right: 1rem;
  }

</style>
